import { AppRoutes } from '../common/constants';
import { Box, IconButton, Link, Menu, MenuItem } from '@mui/material';
import { getFormattedDateTime } from '../common/lib/date';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Icon } from '@iconify/react';
import { JSX, useState } from 'react';
import { ReportItem, ReportState } from '../common/types/reports';
import { useArchiveReport } from '../common/components/mutations';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import DataGrid, { DataGridProps } from '../common/components/dataGrid';
import DataGridLoadingState from '../common/components/dataGrid/dataGridLoadingState';

interface ActionsButtonProps {
  report: ReportItem;
  /**
   * Callback when clicking edit
   */
  onClickEdit: () => void;
  /**
   * Callback when clicking delete
   */
  onClickDelete: () => void;
}

const ActionsButton = ({ onClickEdit, onClickDelete, report }: ActionsButtonProps): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (callback: () => void): void => {
    setAnchorEl(null);
    callback();
  };

  return (
    <Box className={open ? 'active' : ''}>
      <IconButton onClick={(event): void => setAnchorEl(event.currentTarget)}>
        <Icon icon="mdi:dots-vertical" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={(): void => setAnchorEl(null)}>
        <MenuItem onClick={(): void => handleClick(onClickEdit)}>Edit</MenuItem>
        {report.stateID === ReportState.Generated && (
          <MenuItem component={Link} href={`${AppRoutes.Reports}/${report.id}/results`}>
            View
          </MenuItem>
        )}
        <MenuItem onClick={(): void => handleClick(onClickDelete)}>Delete</MenuItem>
      </Menu>
    </Box>
  );
};

/**
 * Render reports table
 */
const Reports = (gridProps: Omit<DataGridProps<ReportItem>, 'columns' | 'rows'>): JSX.Element => {
  const navigate = useNavigate();
  const { mutate: archiveReport, isPending: isArchivingInFlight } = useArchiveReport();

  const { data: reports } = useQuery<ReportItem[]>({
    queryKey: ['/Reports', { stateIDlist: [ReportState.Active, ReportState.Generated].join(',') }],
  });

  const columns: GridColDef<ReportItem>[] = [
    {
      field: 'id',
      headerName: 'Ref',
      width: 80,
    },
    {
      field: 'name',
      headerName: 'Name',
      minWidth: 150,
      flex: 1,
      valueGetter: (_, row) => row.name,
    },
    {
      field: 'desc',
      headerName: 'Description',
      minWidth: 150,
      flex: 2,
      renderCell: ({ value }) => <div className="line-clamp-3">{value}</div>,
    },
    {
      field: 'created',
      headerName: 'Created',
      width: 140,
      valueGetter: (_, row) => getFormattedDateTime(row.created),
    },
    {
      field: 'actions',
      type: 'actions',
      renderCell: ({ row }): JSX.Element => {
        return (
          <ActionsButton
            report={row}
            onClickEdit={(): void => navigate(`${AppRoutes.Reports}/${row.id}`)}
            onClickDelete={(): void => archiveReport(row.id)}
          />
        );
      },
    },
  ];

  return (
    <>
      {reports ? (
        <DataGrid
          {...gridProps}
          loading={isArchivingInFlight}
          rows={reports}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{ field: 'created', sort: 'desc' }],
            },
          }}
        />
      ) : (
        <DataGridLoadingState />
      )}
    </>
  );
};
export default Reports;
